/**
 * Centralized Google Fonts registry for Orbis.
 * Used by: DesignSystemStage, ReviewStage, BrandFoundations
 *
 * Categories help users browse; the flat GOOGLE_FONTS export
 * keeps backward compat with existing code.
 */

export const FONT_CATEGORIES = {
  'Sans Serif': [
    'Inter',
    'Poppins',
    'Open Sans',
    'DM Sans',
    'Space Grotesk',
    'Montserrat',
    'Raleway',
    'Nunito',
    'Nunito Sans',
    'Work Sans',
    'Plus Jakarta Sans',
    'Outfit',
    'Manrope',
    'Lexend',
    'Roboto',
    'Source Sans 3',
    'Quicksand',
    'Varela Round',
    'IBM Plex Sans',
    'Figtree',
    'Geist',
    'Onest',
    'Albert Sans',
    'General Sans',
    'Satoshi',
    'Switzer',
    'Cabinet Grotesk',
    'Urbanist',
    'Sora',
    'Rubik',
    'Karla',
    'Lato',
    'Noto Sans',
    'Mulish',
    'Barlow',
    'Jost',
    'Exo 2',
    'Overpass',
    'Archivo',
    'Red Hat Display',
    'Atkinson Hyperlegible',
    'Commissioner',
    'Bricolage Grotesque',
    'Schibsted Grotesk',
    'Instrument Sans',
    'Hanken Grotesk',
    'Public Sans',
    'Be Vietnam Pro',
    'Chivo',
    'Asap',
  ],
  'Serif': [
    'Playfair Display',
    'Lora',
    'Merriweather',
    'Cormorant Garamond',
    'Libre Baskerville',
    'Newsreader',
    'Cormorant',
    'Fraunces',
    'Literata',
    'Crimson Pro',
    'Spectral',
    'DM Serif Display',
    'DM Serif Text',
    'Noto Serif',
    'Source Serif 4',
    'EB Garamond',
    'Old Standard TT',
    'Cardo',
    'Vollkorn',
    'Bitter',
    'Brygada 1918',
    'Instrument Serif',
    'Young Serif',
    'Gilda Display',
    'Bodoni Moda',
  ],
  'Display': [
    'Bebas Neue',
    'Syne',
    'Fredoka',
    'Abril Fatface',
    'Oswald',
    'Righteous',
    'Unbounded',
    'Big Shoulders Display',
    'Anton',
    'Bungee',
    'Titan One',
    'Protest Guerrilla',
    'Silkscreen',
    'Climate Crisis',
    'Dela Gothic One',
  ],
  'Handwriting': [
    'Caveat',
    'Dancing Script',
    'Pacifico',
    'Satisfy',
    'Kalam',
    'Patrick Hand',
    'Indie Flower',
    'Architects Daughter',
    'Sacramento',
    'Great Vibes',
  ],
  'Monospace': [
    'JetBrains Mono',
    'Space Mono',
    'IBM Plex Mono',
    'Fira Code',
    'Source Code Pro',
    'Inconsolata',
    'Roboto Mono',
    'Ubuntu Mono',
    'Red Hat Mono',
    'Azeret Mono',
  ],
};

/** Flat sorted array — backward compatible with existing GOOGLE_FONTS usage */
export const GOOGLE_FONTS = Object.values(FONT_CATEGORIES).flat().sort();

/** Total font count for display */
export const FONT_COUNT = GOOGLE_FONTS.length;

/**
 * Variable fonts registry — curated list of Google Fonts that support
 * OpenType variable axes, with their supported axis ranges.
 *
 * Source: https://fonts.google.com/variablefonts
 *
 * Keys: axis tags (wght=weight, wdth=width, opsz=optical size, slnt=slant)
 * Values: [min, max] ranges per the Google Fonts spec.
 */
export const VARIABLE_FONTS = {
  // Sans Serif
  'Inter':              { wght: [100, 900] },
  'Roboto Flex':        { wght: [100, 900], wdth: [25, 151], opsz: [8, 144] },
  'DM Sans':            { wght: [100, 900], opsz: [9, 40] },
  'Montserrat':         { wght: [100, 900] },
  'Raleway':            { wght: [100, 900] },
  'Nunito':             { wght: [200, 1000] },
  'Nunito Sans':        { wght: [200, 1000], wdth: [75, 125] },
  'Work Sans':          { wght: [100, 900] },
  'Plus Jakarta Sans':  { wght: [200, 800] },
  'Outfit':             { wght: [100, 900] },
  'Manrope':            { wght: [200, 800] },
  'Lexend':             { wght: [100, 900] },
  'Source Sans 3':      { wght: [200, 900] },
  'IBM Plex Sans':      { wght: [100, 700] },
  'Figtree':            { wght: [300, 900] },
  'Onest':              { wght: [100, 900] },
  'Sora':               { wght: [100, 800] },
  'Rubik':              { wght: [300, 900], slnt: [-12, 0] },
  'Barlow':             { wght: [100, 900] },
  'Exo 2':              { wght: [100, 900] },
  'Cabin':              { wght: [400, 700], wdth: [75, 100] },
  'Josefin Sans':       { wght: [100, 700] },
  'Karla':              { wght: [200, 800] },
  'Mulish':             { wght: [200, 1000] },
  'Jost':               { wght: [100, 900] },
  'Overpass':           { wght: [100, 900] },
  'Chivo':              { wght: [100, 900] },
  'Asap':               { wght: [100, 900], wdth: [75, 125] },
  // Serif
  'Playfair Display':   { wght: [400, 900] },
  'Lora':               { wght: [400, 700] },
  'Merriweather':       { wght: [300, 900] },
  'Fraunces':           { wght: [100, 900], opsz: [9, 144] },
  'Literata':           { wght: [200, 900], opsz: [7, 72] },
  'Crimson Pro':        { wght: [200, 900] },
  'Spectral':           { wght: [200, 800] },
  'Source Serif 4':     { wght: [200, 900], opsz: [8, 60] },
  'Newsreader':         { wght: [200, 800], opsz: [6, 72] },
  'Cormorant':          { wght: [300, 700] },
  'Bodoni Moda':        { wght: [400, 900], opsz: [6, 96] },
  'Vollkorn':           { wght: [400, 900] },
  'Bitter':             { wght: [100, 900] },
  // Display
  'Syne':               { wght: [400, 800] },
  'Oswald':             { wght: [200, 700] },
  'Unbounded':          { wght: [200, 900] },
  'Big Shoulders Display': { wght: [100, 900] },
};

/** Returns true if this font family supports variable axes */
export function isVariableFont(fontFamily) {
  return !!VARIABLE_FONTS[fontFamily];
}

/** Returns the axes config for a font, or null if not variable */
export function getVariableAxes(fontFamily) {
  return VARIABLE_FONTS[fontFamily] || null;
}

/** Human-readable labels for axis tags */
export const AXIS_LABELS = {
  wght: 'Weight',
  wdth: 'Width',
  opsz: 'Optical Size',
  slnt: 'Slant',
};

/**
 * Preload all Google Fonts so previews are instant when the picker opens.
 * Uses Google Fonts CSS2 API with `text=` param to load only "Aa" glyphs first (tiny),
 * then a second pass loads full charsets in the background.
 *
 * Call once on page mount — safe to call multiple times (idempotent).
 */
let _preloaded = false;
export function preloadAllFonts() {
  if (_preloaded || typeof document === 'undefined') return;
  _preloaded = true;

  // Batch into chunks of 8 families per <link> tag (Google Fonts URL limit ~2000 chars)
  const BATCH_SIZE = 8;
  const fonts = [...GOOGLE_FONTS];

  function loadBatch(batch, fullCharset) {
    const families = batch.map(f => `family=${encodeURIComponent(f)}:wght@400;700`).join('&');
    const textParam = fullCharset ? '' : '&text=AaBbCc0123';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap${textParam}`;
    document.head.appendChild(link);
  }

  // Pass 1: load preview glyphs immediately (very small download)
  for (let i = 0; i < fonts.length; i += BATCH_SIZE) {
    loadBatch(fonts.slice(i, i + BATCH_SIZE), false);
  }

  // Pass 2: load full charsets after 2s delay (non-blocking)
  setTimeout(() => {
    for (let i = 0; i < fonts.length; i += BATCH_SIZE) {
      loadBatch(fonts.slice(i, i + BATCH_SIZE), true);
    }
  }, 2000);
}
